import { useState } from 'react';
import { Show } from '@clerk/react';
import { Redirect } from 'wouter';
import {
  useGetMe,
  useGetAdminOverview,
  useRunIngestion,
  useGeneratePuzzles,
  useListAdminPuzzles,
  useReviewPuzzle,
  getGetAdminOverviewQueryKey,
  getListAdminPuzzlesQueryKey,
} from '@workspace/api-client-react';
import { useQueryClient } from '@tanstack/react-query';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Database, Zap, Shield, Clock, CheckCircle2, XCircle, Terminal, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function Admin() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: me, isLoading: meLoading } = useGetMe();
  const { data: overview, isLoading: overviewLoading } = useGetAdminOverview();
  const [puzzleStatus, setPuzzleStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const { data: puzzles, isLoading: puzzlesLoading } = useListAdminPuzzles(
    { status: puzzleStatus },
    { query: { queryKey: getListAdminPuzzlesQueryKey({ status: puzzleStatus }) } }
  );

  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedPuzzle, setSelectedPuzzle] = useState<any>(null);

  const [league, setLeague] = useState('NBA');
  const [gameSlug, setGameSlug] = useState('');
  const [count, setCount] = useState(10);
  const [autoApprove, setAutoApprove] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');

  const runIngestion = useRunIngestion({
    mutation: {
      onSuccess: (data) => {
        toast({ title: 'SUCCESS', description: data.message, className: 'border-primary text-primary font-mono rounded-none uppercase' });
        queryClient.invalidateQueries({ queryKey: getGetAdminOverviewQueryKey() });
        setSyncDialogOpen(false);
      },
      onError: (error: any) => {
        toast({ title: 'ERROR', description: error.message || 'Sync failed', variant: 'destructive', className: 'rounded-none font-mono uppercase border-destructive' });
      },
    },
  });

  const generatePuzzles = useGeneratePuzzles({
    mutation: {
      onSuccess: (data) => {
        toast({ title: 'SUCCESS', description: data.message, className: 'border-primary text-primary font-mono rounded-none uppercase' });
        queryClient.invalidateQueries({ queryKey: getGetAdminOverviewQueryKey() });
        setGenerateDialogOpen(false);
      },
      onError: (error: any) => {
        toast({ title: 'ERROR', description: error.message || 'Generation failed', variant: 'destructive', className: 'rounded-none font-mono uppercase border-destructive' });
      },
    },
  });

  const reviewPuzzle = useReviewPuzzle({
    mutation: {
      onSuccess: () => {
        toast({ title: 'Puzzle reviewed', className: 'border-primary text-primary font-mono rounded-none uppercase' });
        queryClient.invalidateQueries({ queryKey: getListAdminPuzzlesQueryKey({ status: puzzleStatus }) });
        queryClient.invalidateQueries({ queryKey: getGetAdminOverviewQueryKey() });
        setReviewDialogOpen(false);
        setSelectedPuzzle(null);
      },
      onError: (error: any) => {
        toast({ title: 'ERROR', description: error.message || 'Review failed', variant: 'destructive', className: 'rounded-none font-mono uppercase border-destructive' });
      },
    },
  });

  const handleSync = () => {
    runIngestion.mutate({ data: { league } });
  };

  const handleGenerate = () => {
    generatePuzzles.mutate({ data: { gameSlug, count, autoApprove } });
  };

  const handleReview = (action: 'approve' | 'reject') => {
    if (!selectedPuzzle) return;
    reviewPuzzle.mutate({
      puzzleId: selectedPuzzle.id,
      data: {
        action,
        scheduleDate: action === 'approve' && scheduleDate ? scheduleDate : null,
      },
    });
  };

  if (meLoading) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <Navbar />
        <div className="flex items-center justify-center p-12">
          <Skeleton className="h-12 w-48 bg-primary/20 rounded-none" />
        </div>
      </div>
    );
  }

  if (!me?.isAdmin) {
    return (
      <div className="min-h-[100dvh] bg-background">
        <Navbar />
        <div className="flex min-h-[80vh] items-center justify-center p-4">
          <div className="text-center border-2 border-destructive/50 bg-destructive/10 p-16">
            <Shield className="mx-auto mb-6 h-16 w-16 text-destructive animate-pulse" />
            <h1 className="mb-2 text-2xl font-bold font-mono uppercase text-destructive tracking-widest">ACCESS DENIED</h1>
            <p className="text-muted-foreground font-mono uppercase tracking-widest text-sm">
              ADMIN ACCESS REQUIRED
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background">
      <Navbar />

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-12 flex items-center justify-between border-b-2 border-primary/30 pb-6">
          <div>
            <h1 className="mb-2 flex items-center gap-4 text-4xl font-black tracking-tighter sm:text-5xl font-display uppercase text-primary text-shadow-neon-cyan">
              <Terminal className="h-8 w-8 text-primary" />
              ADMIN
            </h1>
            <p className="text-muted-foreground font-mono uppercase tracking-widest mt-2">MANAGE GAMES AND PUZZLES</p>
          </div>
          <Badge className="bg-primary/20 text-primary border-primary rounded-none font-mono uppercase tracking-widest glow-primary text-lg px-4 py-2">
            <Shield className="mr-2 h-5 w-5" />
            SYSADMIN
          </Badge>
        </div>

        {overviewLoading ? (
          <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-none bg-muted/50 border border-primary/20" />
              ))}
            </div>
          </div>
        ) : overview ? (
          <>
            {/* At-risk pool alert */}
            {overview.pools.some((p) => p.atRisk) && (
              <div
                className="mb-12 border-2 border-destructive bg-destructive/10 p-6 shadow-[0_0_40px_rgba(255,0,0,0.15)]"
                data-testid="banner-pool-at-risk"
              >
                <div className="mb-3 flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-destructive animate-pulse" />
                  <h2 className="text-xl font-black font-display uppercase tracking-widest text-destructive">
                    PUZZLE SUPPLY AT RISK
                  </h2>
                </div>
                <p className="mb-4 text-sm font-mono uppercase tracking-widest text-muted-foreground">
                  These games are running low on approved puzzles and the scheduler cannot refill them automatically. Without action, their daily puzzle will go dark.
                </p>
                <div className="space-y-2">
                  {overview.pools.filter((p) => p.atRisk).map((pool) => (
                    <div
                      key={pool.gameSlug}
                      className="flex flex-col gap-1 border border-destructive/50 bg-background/50 p-3 sm:flex-row sm:items-center sm:justify-between"
                      data-testid={`alert-pool-${pool.gameSlug}`}
                    >
                      <span className="font-bold font-mono text-sm uppercase tracking-widest">
                        {pool.gameTitle} — {pool.approvedUnscheduled} LEFT IN RESERVE
                      </span>
                      <span className="text-xs font-mono uppercase tracking-widest text-destructive">
                        {pool.canGenerate
                          ? 'FIX: SYNC LEAGUE DATA, THEN AUTO-GENERATE PUZZLES'
                          : 'FIX: ADD & APPROVE PUZZLES MANUALLY (TYPE CANNOT BE AUTO-GENERATED)'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="mb-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div className="border border-primary/30 bg-primary/5 p-6 hover:bg-primary/10 transition-colors">
                <div className="mb-2 text-4xl font-black text-primary font-display">
                  {overview.teamCount}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">TEAMS</div>
              </div>
              <div className="border border-primary/30 bg-primary/5 p-6 hover:bg-primary/10 transition-colors">
                <div className="mb-2 text-4xl font-black text-primary font-display">
                  {overview.playerCount}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">PLAYERS</div>
              </div>
              <div className="border border-secondary/50 bg-secondary/10 p-6 hover:bg-secondary/20 transition-colors glow-secondary">
                <div className="mb-2 text-4xl font-black text-secondary font-display">
                  {overview.pendingPuzzles}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">PENDING REVIEW</div>
              </div>
              <div className="border border-primary/30 bg-primary/5 p-6 hover:bg-primary/10 transition-colors">
                <div className="mb-2 text-4xl font-black text-primary font-display">
                  {overview.approvedPuzzles}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">APPROVED</div>
              </div>
              <div className="border border-primary/30 bg-primary/5 p-6 hover:bg-primary/10 transition-colors">
                <div className="mb-2 text-4xl font-black text-primary font-display">
                  {overview.totalPlays}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">PLAYS</div>
              </div>
              <div className="border border-primary/30 bg-primary/5 p-6 hover:bg-primary/10 transition-colors">
                <div className="mb-2 text-4xl font-black text-primary font-display">
                  {overview.userCount}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">USERS</div>
              </div>
            </div>

            {/* Actions */}
            <div className="mb-12 grid gap-6 sm:grid-cols-2">
              <Dialog open={syncDialogOpen} onOpenChange={setSyncDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" variant="outline" className="h-auto flex-col items-start p-8 text-left rounded-none border-2 border-primary/50 text-primary hover:bg-primary/10 hover:border-primary transition-all" data-testid="button-sync-data">
                    <div className="mb-4 flex items-center gap-3 font-bold font-display uppercase tracking-widest text-2xl">
                      <Database className="h-6 w-6" />
                      SYNC LEAGUE DATA
                    </div>
                    <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">
                      IMPORT TEAMS AND PLAYERS FROM EXTERNAL API
                    </p>
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none border-2 border-primary bg-background shadow-[0_0_40px_rgba(0,217,255,0.2)]">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-widest text-2xl text-primary text-shadow-neon-cyan">SYNC LEAGUE DATA</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div>
                      <Label htmlFor="league" className="font-mono uppercase tracking-widest text-muted-foreground text-xs mb-2 block">LEAGUE</Label>
                      <Select value={league} onValueChange={setLeague}>
                        <SelectTrigger id="league" className="rounded-none border-primary/50 bg-primary/5 font-mono text-lg uppercase h-12" data-testid="select-league">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none border-primary bg-background font-mono uppercase">
                          <SelectItem value="NBA">NBA</SelectItem>
                          <SelectItem value="NFL">NFL</SelectItem>
                          <SelectItem value="MLB">MLB</SelectItem>
                          <SelectItem value="NHL">NHL</SelectItem>
                          <SelectItem value="EPL">EPL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleSync} disabled={runIngestion.isPending} className="w-full rounded-none font-bold uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 glow-primary h-12" data-testid="button-run-sync">
                      {runIngestion.isPending ? 'SYNCING...' : 'RUN SYNC'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" variant="outline" className="h-auto flex-col items-start p-8 text-left rounded-none border-2 border-secondary/50 text-secondary hover:bg-secondary/10 hover:border-secondary transition-all" data-testid="button-generate-puzzles">
                    <div className="mb-4 flex items-center gap-3 font-bold font-display uppercase tracking-widest text-2xl">
                      <Zap className="h-6 w-6 animate-pulse" />
                      AUTO-GENERATE PUZZLES
                    </div>
                    <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">
                      GENERATE NEW PUZZLES AUTOMATICALLY
                    </p>
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none border-2 border-secondary bg-background shadow-[0_0_40px_rgba(176,38,255,0.2)]">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase tracking-widest text-2xl text-secondary text-shadow-neon-purple">GENERATE PUZZLES</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div>
                      <Label htmlFor="gameSlug" className="font-mono uppercase tracking-widest text-muted-foreground text-xs mb-2 block">GAME</Label>
                      <Input
                        id="gameSlug"
                        value={gameSlug}
                        onChange={(e) => setGameSlug(e.target.value)}
                        placeholder="E.G. LOGO-REVEAL-NBA"
                        className="rounded-none border-secondary/50 bg-secondary/5 font-mono text-lg uppercase focus-visible:ring-secondary focus-visible:border-secondary h-12"
                        data-testid="input-game-slug"
                      />
                    </div>
                    <div>
                      <Label htmlFor="count" className="font-mono uppercase tracking-widest text-muted-foreground text-xs mb-2 block">ITERATIONS (1-50)</Label>
                      <Input
                        id="count"
                        type="number"
                        min="1"
                        max="50"
                        value={count}
                        onChange={(e) => setCount(Number(e.target.value))}
                        className="rounded-none border-secondary/50 bg-secondary/5 font-mono text-lg uppercase focus-visible:ring-secondary focus-visible:border-secondary h-12"
                        data-testid="input-count"
                      />
                    </div>
                    <div className="flex items-center gap-3 border border-secondary/30 p-4 bg-secondary/5">
                      <input
                        type="checkbox"
                        id="autoApprove"
                        checked={autoApprove}
                        onChange={(e) => setAutoApprove(e.target.checked)}
                        className="h-5 w-5 accent-secondary"
                        data-testid="checkbox-auto-approve"
                      />
                      <Label htmlFor="autoApprove" className="cursor-pointer font-mono uppercase tracking-widest font-bold text-secondary">
                        SKIP MANUAL REVIEW
                      </Label>
                    </div>
                    <Button
                      onClick={handleGenerate}
                      disabled={generatePuzzles.isPending || !gameSlug}
                      className="w-full rounded-none font-bold uppercase tracking-widest bg-secondary text-secondary-foreground hover:bg-secondary/90 glow-secondary h-12"
                      data-testid="button-run-generate"
                    >
                      {generatePuzzles.isPending ? 'GENERATING...' : 'RUN GENERATION'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 mb-12">
              {/* League Status */}
              {overview.leagues.length > 0 && (
                <div>
                  <h2 className="mb-6 text-2xl font-bold font-display uppercase tracking-widest border-b border-primary/30 pb-2">
                    LEAGUE DATA STATUS
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {overview.leagues.map((league) => (
                      <div key={league.league} className="border border-card-border bg-card p-4 hover:border-primary/50 transition-colors">
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="font-black text-2xl font-display">{league.league}</h3>
                          <Badge variant="outline" className="text-[10px] font-mono rounded-none uppercase tracking-widest bg-background text-primary border-primary/50">{league.sport}</Badge>
                        </div>
                        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                          {league.teamCount} TEAMS // {league.playerCount} PLAYERS
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Daily Puzzle Pools */}
              {overview.pools.length > 0 && (
                <div>
                  <h2 className="mb-6 text-2xl font-bold font-display uppercase tracking-widest border-b border-primary/30 pb-2">
                    DAILY PUZZLE POOLS
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {overview.pools.map((pool) => (
                      <div
                        key={pool.gameSlug}
                        className="border border-card-border bg-card p-4 hover:border-primary/50 transition-colors"
                        data-testid={`pool-${pool.gameSlug}`}
                      >
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <h3 className="font-bold font-mono text-sm tracking-widest uppercase">{pool.gameTitle}</h3>
                          {pool.atRisk ? (
                            <Badge className="bg-destructive text-destructive-foreground rounded-none text-[10px] uppercase font-mono tracking-widest font-bold animate-pulse">AT RISK</Badge>
                          ) : pool.low ? (
                            <Badge className="bg-secondary/30 text-secondary border border-secondary/50 rounded-none text-[10px] uppercase font-mono tracking-widest font-bold">LOW STOCK</Badge>
                          ) : (
                            <Badge className="bg-primary/20 text-primary border-none rounded-none text-[10px] uppercase font-mono tracking-widest font-bold">HEALTHY</Badge>
                          )}
                        </div>
                        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                          {pool.approvedUnscheduled} IN RESERVE
                          {!pool.canGenerate && ' (manual only)'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Puzzle Review Queue */}
            <div>
              <h2 className="mb-6 text-2xl font-bold font-display uppercase tracking-widest border-b border-primary/30 pb-2">
                REVIEW QUEUE
              </h2>

              <Tabs value={puzzleStatus} onValueChange={(v) => setPuzzleStatus(v as any)}>
                <TabsList className="mb-8 bg-background border border-primary/30 rounded-none h-14 p-1 gap-1 inline-flex w-full sm:w-auto">
                  <TabsTrigger value="pending" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:glow-secondary px-6" data-testid="tab-pending">
                    <Clock className="mr-2 h-4 w-4" />
                    PENDING ({overview.pendingPuzzles})
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400 px-6" data-testid="tab-approved">
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    APPROVED
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="rounded-none font-bold uppercase tracking-widest font-mono data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400 px-6" data-testid="tab-rejected">
                    <XCircle className="mr-2 h-4 w-4" />
                    REJECTED
                  </TabsTrigger>
                </TabsList>

                <TabsContent value={puzzleStatus}>
                  {puzzlesLoading ? (
                    <div className="space-y-4">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-24 rounded-none border border-primary/20 bg-muted/50" />
                      ))}
                    </div>
                  ) : puzzles && puzzles.length > 0 ? (
                    <div className="space-y-4">
                      {puzzles.map((puzzle) => (
                        <div
                          key={puzzle.id}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-card-border bg-card p-5 hover:border-primary/50 transition-colors"
                        >
                          <div className="flex-1">
                            <div className="mb-2 flex flex-wrap items-center gap-3">
                              <h3 className="font-bold text-lg font-display uppercase tracking-widest text-primary">{puzzle.gameTitle}</h3>
                              <Badge variant="outline" className="text-[10px] font-mono rounded-none uppercase tracking-widest border-primary/50 text-primary bg-background">{puzzle.mode}</Badge>
                            </div>
                            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                              {puzzle.rounds.length} ROUNDS // CREATED: {new Date(puzzle.createdAt).toLocaleDateString()}
                              {puzzle.puzzleDate && ` // SCHEDULED: ${puzzle.puzzleDate}`}
                            </p>
                          </div>
                          {puzzleStatus === 'pending' && (
                            <Dialog open={reviewDialogOpen && selectedPuzzle?.id === puzzle.id} onOpenChange={(open) => {
                              setReviewDialogOpen(open);
                              if (!open) setSelectedPuzzle(null);
                            }}>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="w-full sm:w-auto rounded-none font-bold uppercase tracking-widest border-secondary/50 text-secondary hover:bg-secondary/10 hover:border-secondary"
                                  onClick={() => setSelectedPuzzle(puzzle)}
                                  data-testid={`button-review-${puzzle.id}`}
                                >
                                  REVIEW PUZZLE
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-none border-2 border-secondary bg-background shadow-[0_0_40px_rgba(176,38,255,0.2)]">
                                <DialogHeader>
                                  <DialogTitle className="font-display uppercase tracking-widest text-2xl text-secondary text-shadow-neon-purple">REVIEW: {puzzle.gameTitle}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-6 py-4">
                                  {puzzle.rounds.map((round) => (
                                    <div key={round.index} className="border border-card-border bg-muted/30 p-6 relative">
                                      <div className="absolute top-0 left-0 bg-primary/20 text-primary font-mono text-xs px-2 py-1 uppercase font-bold">RND_{round.index + 1}</div>
                                      <p className="mt-4 mb-4 text-xl font-display font-bold uppercase tracking-widest">{round.prompt}</p>
                                      {round.imageUrl && (
                                        <div className="mb-4 bg-black p-2 inline-block border border-primary/30">
                                          <img src={round.imageUrl} alt="" className="max-h-48" />
                                        </div>
                                      )}
                                      <div className="space-y-2 text-sm font-mono uppercase">
                                        <div className="font-bold text-muted-foreground text-xs mb-3">CHOICES:</div>
                                        {round.choices.map((choice, idx) => (
                                          <div
                                            key={idx}
                                            className={`pl-3 border-l-2 ${choice === round.answer ? 'border-green-500 font-bold text-green-400 bg-green-500/10 py-1' : 'border-card-border text-muted-foreground'}`}
                                          >
                                            {choice} {choice === round.answer && '<-- EXPECTED'}
                                          </div>
                                        ))}
                                      </div>
                                      <div className="mt-4 text-xs font-mono text-muted-foreground uppercase">
                                        Max: {round.maxPoints} pts
                                      </div>
                                    </div>
                                  ))}
                                  <div className="bg-primary/5 p-6 border border-primary/30 mt-8">
                                    <Label htmlFor="scheduleDate" className="font-mono uppercase tracking-widest text-primary text-xs mb-2 block">SCHEDULE DATE (OPTIONAL)</Label>
                                    <Input
                                      id="scheduleDate"
                                      type="date"
                                      value={scheduleDate}
                                      onChange={(e) => setScheduleDate(e.target.value)}
                                      className="rounded-none border-primary/50 bg-background font-mono uppercase h-12"
                                      data-testid="input-schedule-date"
                                    />
                                  </div>
                                  <div className="flex gap-4 pt-4 border-t border-border/50">
                                    <Button
                                      onClick={() => handleReview('reject')}
                                      disabled={reviewPuzzle.isPending}
                                      variant="destructive"
                                      className="flex-1 rounded-none font-bold uppercase tracking-widest h-14"
                                      data-testid="button-reject"
                                    >
                                      <XCircle className="mr-2 h-5 w-5" /> REJECT
                                    </Button>
                                    <Button
                                      onClick={() => handleReview('approve')}
                                      disabled={reviewPuzzle.isPending}
                                      className="flex-[2] rounded-none font-bold uppercase tracking-widest bg-green-600 hover:bg-green-700 text-white h-14"
                                      data-testid="button-approve"
                                    >
                                      <CheckCircle2 className="mr-2 h-5 w-5" /> APPROVE
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-primary/30 bg-primary/5 p-16 text-center">
                      <p className="text-xl font-mono text-primary animate-pulse uppercase tracking-widest">NO {puzzleStatus} PUZZLES IN QUEUE</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
